import ky from 'ky';
import type { KyInstance, Options, HTTPError } from 'ky';
import type { IAuthSDK } from './AuthSDK';
import type { IMMBaseClient } from './IMMBaseClient';

type KyError = HTTPError & { data?: any };

// Более общий клиент для старого рест апи
export class MMBaseClient implements IMMBaseClient {
  protected kyInstance: KyInstance;

  constructor(
    private baseURL: string,
    private authSDK?: IAuthSDK
  ) {
    this.kyInstance = ky.create({
      prefixUrl: baseURL,
      timeout: 10000,
      hooks: {
        beforeRequest: [this.beforeRequestHook.bind(this)],
        beforeError: [this.beforeErrorHook.bind(this)],
        // afterResponse: [this.afterResponseHook.bind(this)], TODO: возможно нужно будет добавить разавторизацию на 401 при 2 и более ошибках.
        // UserStory: пользователь написал в поддержку, что его аккаунт скомпрометирован. Токены добавили в исключения. Следовательно надо разавторизовать пользователя.
      },
    });
  }

  private async beforeRequestHook(request: Request): Promise<void> {
    const token = await this.authSDK?.getToken();
    if (token) {
      request.headers.set('Authorization', `Bearer ${token}`);
    }
  }

  private async beforeErrorHook(error: HTTPError) {
    const kyError = error as KyError;
    kyError.data = await this.safeJson(error.response);

    return kyError;
  }

  protected async safeJson<T>(response: Response): Promise<T> {
    const clone = response.clone();
    const text = await clone.text();

    return text.trim() ? (JSON.parse(text) as T) : (null as T);
  }

  public async get<T>(url: string, options?: Options): Promise<T> {
    const response = await this.kyInstance.get(url, options);
    return this.safeJson<T>(response);
  }

  public async post<T, D = any>(url: string, data?: D, options?: Options): Promise<T> {
    const response = await this.kyInstance.post(url, {
      ...options,
      json: data,
    });
    return this.safeJson<T>(response);
  }

  public async put<T, D = any>(url: string, data?: D, options?: Options): Promise<T> {
    const response = await this.kyInstance.put(url, {
      ...options,
      json: data,
    });
    return this.safeJson<T>(response);
  }

  public async patch<T, D = any>(url: string, data?: D, options?: Options): Promise<T> {
    const response = await this.kyInstance.patch(url, {
      ...options,
      json: data,
    });
    return this.safeJson<T>(response);
  }

  public async delete<T>(url: string, options?: Options): Promise<T> {
    const response = await this.kyInstance.delete(url, options);
    return this.safeJson<T>(response);
  }
}
